const Modal = ({ loading, show, onClose, formData }) => {
    if (!show) return null;

    // Function to format field names
    const formatFieldName = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/\bid\b/gi, '')
            .trim()
            .replace(/^./, (str) => str.toUpperCase());
    };

    // Function to make URLs clickable
    const makeLink = (url) => {
        return (
            <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all"
            >
                {url}
            </a>
        );
    };

    // Function to safely render any value
    const renderValue = (value, key) => {
        // Handle null/undefined
        if (value === null || value === undefined) {
            return "N/A";
        }

        // Handle URLs
        if (typeof value === 'string' && value.startsWith('http')) {
            return makeLink(value);
        }

        // Handle string values (including JSON strings)
        if (typeof value === 'string') {
            // Try to parse JSON strings
            if (value.startsWith('{') || value.startsWith('[')) {
                try {
                    const parsed = JSON.parse(value);
                    return renderValue(parsed, key);
                } catch {
                    return value;
                }
            }
            // Handle regular strings
            return value;
        }

        // Handle numbers, booleans
        if (typeof value !== 'object') {
            return value.toString();
        }

        // Handle arrays
        if (Array.isArray(value)) {
            if (value.length === 0) return "None";
            
            return (
                <div className="space-y-2 ml-2">
                    {value.map((item, index) => (
                        <div key={index} className="border-l-2 pl-2 border-gray-200">
                            {renderValue(item, `${key}[${index}]`)}
                        </div>
                    ))}
                </div>
            );
        }

        // Handle objects
        return (
            <div className="space-y-1 ml-2">
                {Object.entries(value)
                    .filter(([k]) => k !== 'id') // Exclude id fields
                    .map(([subKey, subValue]) => (
                        <div key={subKey}>
                            <span className="font-semibold">{formatFieldName(subKey)}:</span> {renderValue(subValue, subKey)}
                        </div>
                    ))}
            </div>
        );
    };

    // Fields to exclude completely
    const excludedFields = [
        'id', 'academic_year_id', 'parent_id', 'class_id', 
        'blood_group_id', 'board_id', 'caste_category_id',
        'country_id', 'gender_id', 'language_id', 'medium_id',
        'co_curricular_activity_id', 'student_photo',
    ];

    // Fields to display after the photo (regular fields)
    const regularFields = [
        'application_no', 'first_name', 'last_name', 'dob',
        'gender', 'status', 'mobile', 'email', 'address1',
        'city', 'state', 'zip_code'
    ];

    // Custom labels for specific fields
    const customLabels = {
        'class': 'Class',
        'academic_year': 'Academic Year',
        'bloodgroup': 'Blood Group',
        'board': 'Board',
        'caste_category': 'Caste Category',
        'co_curricular_activities': 'Co-curricular Activity',
        'country': 'Country',
        'gender': 'Gender',
        'medium': 'Medium',
        'parent': 'Parent Details',
        'extra_curricular_activities': 'Extra Curricular Activities',
        'payment_details': 'Payment Details'
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-sky-200 bg-opacity-50 px-4">
            <div className="bg-white p-5 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
                <h2 className="text-xl font-bold mb-4 text-center">Application Details</h2>

                {loading ? (
                    <p className="text-center">Loading...</p>
                ) : formData?.error ? (
                    <p className="text-center text-red-500">{formData.error}</p>
                ) : (
                    <div className="overflow-auto max-h-[60vh]">
                        {/* Student Photo at the top */}
                        {formData?.student_photo_url && (
                            <div className="flex justify-center mb-4">
                                <img 
                                    src={formData.student_photo_url} 
                                    alt="Student" 
                                    className="h-32 w-32 object-cover rounded-full border-4 border-white shadow"
                                />
                            </div>
                        )}

                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <tbody>
                                {/* Render regular fields first */}
                                {regularFields
                                    .filter(field => formData && field in formData)
                                    .map(field => (
                                        <tr key={field} className="border">
                                            <td className="border px-3 py-2 font-semibold w-1/3">
                                                {customLabels[field] || formatFieldName(field)}:
                                            </td>
                                            <td className="border px-3 py-2">
                                                {renderValue(formData[field], field)}
                                            </td>
                                        </tr>
                                    ))}

                                {/* Render remaining fields */}
                                {Object.entries(formData || {})
                                    .filter(([key]) => 
                                        !excludedFields.includes(key) && 
                                        !regularFields.includes(key)
                                    )
                                    .map(([key, value]) => (
                                        <tr key={key} className="border">
                                            <td className="border px-3 py-2 font-semibold w-1/3">
                                                {customLabels[key] || formatFieldName(key)}:
                                            </td>
                                            <td className="border px-3 py-2">
                                                {renderValue(value, key)}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="mt-4 flex justify-end gap-3">
                    <button 
                        onClick={onClose} 
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Modal;